import { useMemo } from 'react';
import type {
  ChartViewId,
  CustomTimeSegment,
  SpectralWorkspaceSession,
} from '../types';
import { formatSegmentTabLabel } from '../utils/customSegment';
import { computeDescriptiveStatistics } from '../utils/computeDescriptiveStatistics';
import {
  getWorkspaceAvailableSeconds,
  resolveSegmentBounds,
} from '../utils/resolveSpectraSegment';

type UseDescriptiveStatisticsParams = {
  readySessions: SpectralWorkspaceSession[];
  activeViewId: ChartViewId;
  customSegments: CustomTimeSegment[];
  trendStart: string | null;
  trendEnd: string | null;
};

export const useDescriptiveStatistics = ({
  readySessions,
  activeViewId,
  customSegments,
  trendStart,
  trendEnd,
}: UseDescriptiveStatisticsParams) => {
  const availableSeconds = useMemo(
    () => getWorkspaceAvailableSeconds(readySessions),
    [readySessions]
  );

  const maxAvailableSecond = useMemo(
    () =>
      availableSeconds.length > 0
        ? availableSeconds[availableSeconds.length - 1]
        : 0,
    [availableSeconds]
  );

  const segmentBounds = useMemo(
    () =>
      resolveSegmentBounds({
        activeViewId,
        customSegments,
        availableSeconds,
        trendStart,
        trendEnd,
      }),
    [activeViewId, availableSeconds, customSegments, trendEnd, trendStart]
  );

  const rows = useMemo(() => {
    if (!segmentBounds) return [];
    return computeDescriptiveStatistics(
      readySessions,
      segmentBounds.startSecond,
      segmentBounds.endSecond
    );
  }, [readySessions, segmentBounds]);

  const segmentLabel = useMemo(() => {
    if (activeViewId === 'full') return 'Tratto completo';
    const segment = customSegments.find(item => item.id === activeViewId);
    return segment ? formatSegmentTabLabel(segment) : 'Tratto custom';
  }, [activeViewId, customSegments]);

  return {
    availableSeconds,
    maxAvailableSecond,
    segmentBounds,
    rows,
    segmentLabel,
  };
};
